import { dataDictionaryViewActions } from './data-dictionary-view.actions';

const reportSlice = { data: [], summary: null, isLoading: false, error: null };
const usageSlice = { data: [], isLoading: false, isRefreshing: false, lastRefreshedAt: null, refreshedBy: null, error: null };

const initialState = {
  objects: [],
  objectDetails: null,
  objectRelated: null,
  isLoadingRelated: false,
  objectHistory: null,
  isLoadingHistory: false,
  categories: [],
  schemas: [],
  isLoading: [],
  error: [],
  selectedLetter: 'a',
  categoryFilter: [],
  schemaFilter: [],
  searchTerm: '',
  availableLetters: [],
  reports: {
    pii: { ...reportSlice },
    aiEligible: { ...reportSlice },
    ownership: { ...reportSlice },
    schemaAudit: { ...reportSlice },
    usage: { ...usageSlice },
  }
};

function removeItemFromArray(arr, item) {
  return arr.filter(e => e !== item);
}

export default function dataDictionaryViewReducer(state = initialState, action) {
  let status = '';
  
  switch (action.type) {
    //-------Load Objects Request-------------
    case dataDictionaryViewActions.LOAD_OBJECTS_REQUEST:
      return {
        ...state,
        error: [],
        isLoading: [...state.isLoading, 'objects']
      };

    //-------Load Objects Success-------------
    case dataDictionaryViewActions.LOAD_OBJECTS_SUCCESS:
      // API response is now simplified with direct object array
      const apiResponse = action.payload || {};
      const objects = apiResponse.response.objects || [];
      
      // Map API fields to component fields
      const mappedObjects = Array.isArray(objects) ? objects.map(obj => ({
        ...obj,
        displayName: obj.name || 'Unnamed',
        sourceName: obj.instanceName || 'Unknown',
        type: obj.type || 'Unknown',
        businessContext: obj.businessContext || 'Not Available'
      })) : [];
      
      return {
        ...state,
        objects: mappedObjects,
        availableLetters: apiResponse.response.availableLetters || [],
        isLoading: removeItemFromArray(state.isLoading, 'objects')
      };

    //-------Load Objects Failure-------------
    case dataDictionaryViewActions.LOAD_OBJECTS_FAILURE:
      status = '';
      action.payload.response === undefined ? status = '404' : status = action.payload.response.status;
      return {
        ...state,
        isLoading: removeItemFromArray(state.isLoading, 'objects'),
        error: [...state.error, status]
      };

    //-------Search Objects Request-------------
    case dataDictionaryViewActions.SEARCH_OBJECTS_REQUEST:
      return {
        ...state,
        error: [],
        isLoading: [...state.isLoading, 'objects']
      };

    //-------Search Objects Success-------------
    case dataDictionaryViewActions.SEARCH_OBJECTS_SUCCESS:
      const searchResponse = action.payload || {};
      const searchObjects = searchResponse.response.objects || [];
      
      const mappedSearchObjects = Array.isArray(searchObjects) ? searchObjects.map(obj => ({
        ...obj,
        displayName: obj.name || 'Unnamed',
        sourceName: obj.instanceName || 'Unknown',
        type: obj.type || 'Unknown',
        businessContext: obj.businessContext || 'Not Available'
      })) : [];
      
      return {
        ...state,
        objects: mappedSearchObjects,
        availableLetters: [],
        isLoading: removeItemFromArray(state.isLoading, 'objects')
      };

    //-------Search Objects Failure-------------
    case dataDictionaryViewActions.SEARCH_OBJECTS_FAILURE:
      status = '';
      action.payload.response === undefined ? status = '404' : status = action.payload.response.status;
      return {
        ...state,
        isLoading: removeItemFromArray(state.isLoading, 'objects'),
        error: [...state.error, status]
      };

    //-------Load Object Details Request-------------
    case dataDictionaryViewActions.LOAD_OBJECT_DETAILS_REQUEST:
      return {
        ...state,
        error: [],
        objectDetails: null,
        isLoading: [...state.isLoading, 'objectdetails']
      };

    //-------Load Object Details Success-------------
    case dataDictionaryViewActions.LOAD_OBJECT_DETAILS_SUCCESS:
      const detailsResponse = action.payload.response.objects;
      // If objects is an array, take the first element, otherwise use as is
      const objectDetails = Array.isArray(detailsResponse) && detailsResponse.length > 0 
        ? detailsResponse[0] 
        : detailsResponse;
      
      return {
        ...state,
        objectDetails: objectDetails,
        isLoading: removeItemFromArray(state.isLoading, 'objectdetails')
      };

    //-------Load Object Details Failure-------------
    case dataDictionaryViewActions.LOAD_OBJECT_DETAILS_FAILURE:
      status = '';
      action.payload.response === undefined ? status = '404' : status = action.payload.response.status;
      return {
        ...state,
        objectDetails: null,
        isLoading: removeItemFromArray(state.isLoading, 'objectdetails'),
        error: [...state.error, status]
      };

    case dataDictionaryViewActions.LOAD_OBJECT_RELATED_REQUEST:
      return {
        ...state,
        objectRelated: null,
        isLoadingRelated: true
      };

    case dataDictionaryViewActions.LOAD_OBJECT_RELATED_SUCCESS:
      return {
        ...state,
        objectRelated: action.payload || null,
        isLoadingRelated: false
      };

    case dataDictionaryViewActions.LOAD_OBJECT_RELATED_FAILURE:
      return {
        ...state,
        objectRelated: null,
        isLoadingRelated: false
      };

    case dataDictionaryViewActions.LOAD_OBJECT_HISTORY_REQUEST:
      return {
        ...state,
        objectHistory: null,
        isLoadingHistory: true
      };

    case dataDictionaryViewActions.LOAD_OBJECT_HISTORY_SUCCESS:
      return {
        ...state,
        objectHistory: action.payload || null,
        isLoadingHistory: false
      };

    case dataDictionaryViewActions.LOAD_OBJECT_HISTORY_FAILURE:
      return {
        ...state,
        objectHistory: null,
        isLoadingHistory: false
      };

    //-------Set Selected Letter-------------
    case dataDictionaryViewActions.SET_SELECTED_LETTER:
      return {
        ...state,
        selectedLetter: action.payload
      };

    //-------Set Category Filter-------------
    case dataDictionaryViewActions.SET_CATEGORY_FILTER:
      return {
        ...state,
        categoryFilter: action.payload
      };

    //-------Set Search Term-------------
    case dataDictionaryViewActions.SET_SEARCH_TERM:
      return {
        ...state,
        searchTerm: action.payload || ''
      };

    //-------Load Categories Request-------------
    case dataDictionaryViewActions.LOAD_CATEGORIES_REQUEST:
      return {
        ...state,
        error: [],
        isLoading: [...state.isLoading, 'categories']
      };

    //-------Load Categories Success-------------
    case dataDictionaryViewActions.LOAD_CATEGORIES_SUCCESS:
      const categories = action.payload.response.types || [];
      return {
        ...state,
        categories: categories,
        isLoading: removeItemFromArray(state.isLoading, 'categories')
      };

    //-------Load Categories Failure-------------
    case dataDictionaryViewActions.LOAD_CATEGORIES_FAILURE:
      status = '';
      action.payload.response === undefined ? status = '404' : status = action.payload.response.status;
      return {
        ...state,
        isLoading: removeItemFromArray(state.isLoading, 'categories'),
        error: [...state.error, status]
      };

    //-------Load Schemas Request-------------
    case dataDictionaryViewActions.LOAD_SCHEMAS_REQUEST:
      return {
        ...state,
        error: [],
        isLoading: [...state.isLoading, 'schemas']
      };

    //-------Load Schemas Success-------------
    case dataDictionaryViewActions.LOAD_SCHEMAS_SUCCESS:
      const schemas = action.payload.response || [];
      return {
        ...state,
        schemas: schemas,
        isLoading: removeItemFromArray(state.isLoading, 'schemas')
      };

    //-------Load Schemas Failure-------------
    case dataDictionaryViewActions.LOAD_SCHEMAS_FAILURE:
      status = '';
      action.payload.response === undefined ? status = '404' : status = action.payload.response.status;
      return {
        ...state,
        isLoading: removeItemFromArray(state.isLoading, 'schemas'),
        error: [...state.error, status]
      };

    //-------Set Schema Filter-------------
    case dataDictionaryViewActions.SET_SCHEMA_FILTER:
      return {
        ...state,
        schemaFilter: action.payload
      };

    // ── Report reducers ────────────────────────────────────────────────────────

    case dataDictionaryViewActions.LOAD_PII_REPORT_REQUEST:
      return { ...state, reports: { ...state.reports, pii: { data: [], summary: null, isLoading: true, error: null } } };
    case dataDictionaryViewActions.LOAD_PII_REPORT_SUCCESS:
      return { ...state, reports: { ...state.reports, pii: { data: (action.payload && action.payload.response) || [], summary: (action.payload && action.payload.summary) || null, isLoading: false, error: null } } };
    case dataDictionaryViewActions.LOAD_PII_REPORT_FAILURE:
      return { ...state, reports: { ...state.reports, pii: { data: [], summary: null, isLoading: false, error: 'Failed to load PII report' } } };

    case dataDictionaryViewActions.LOAD_AI_ELIGIBLE_REPORT_REQUEST:
      return { ...state, reports: { ...state.reports, aiEligible: { data: [], isLoading: true, error: null } } };
    case dataDictionaryViewActions.LOAD_AI_ELIGIBLE_REPORT_SUCCESS:
      return { ...state, reports: { ...state.reports, aiEligible: { data: (action.payload && action.payload.response) || [], isLoading: false, error: null } } };
    case dataDictionaryViewActions.LOAD_AI_ELIGIBLE_REPORT_FAILURE:
      return { ...state, reports: { ...state.reports, aiEligible: { data: [], isLoading: false, error: 'Failed to load AI Sensitive report' } } };

    case dataDictionaryViewActions.LOAD_OWNERSHIP_REPORT_REQUEST:
      return { ...state, reports: { ...state.reports, ownership: { data: [], isLoading: true, error: null } } };
    case dataDictionaryViewActions.LOAD_OWNERSHIP_REPORT_SUCCESS:
      return { ...state, reports: { ...state.reports, ownership: { data: (action.payload && action.payload.response) || [], isLoading: false, error: null } } };
    case dataDictionaryViewActions.LOAD_OWNERSHIP_REPORT_FAILURE:
      return { ...state, reports: { ...state.reports, ownership: { data: [], isLoading: false, error: 'Failed to load Ownership report' } } };

    case dataDictionaryViewActions.LOAD_SCHEMA_AUDIT_REPORT_REQUEST:
      return { ...state, reports: { ...state.reports, schemaAudit: { data: [], isLoading: true, error: null } } };
    case dataDictionaryViewActions.LOAD_SCHEMA_AUDIT_REPORT_SUCCESS:
      return { ...state, reports: { ...state.reports, schemaAudit: { data: (action.payload && action.payload.response) || [], isLoading: false, error: null } } };
    case dataDictionaryViewActions.LOAD_SCHEMA_AUDIT_REPORT_FAILURE:
      return { ...state, reports: { ...state.reports, schemaAudit: { data: [], isLoading: false, error: 'Failed to load Schema Audit report' } } };

    case dataDictionaryViewActions.LOAD_USAGE_REPORT_REQUEST:
      return { ...state, reports: { ...state.reports, usage: { ...state.reports.usage, isLoading: true, error: null } } };
    case dataDictionaryViewActions.LOAD_USAGE_REPORT_SUCCESS:
      return { ...state, reports: { ...state.reports, usage: {
        data: (action.payload && action.payload.response) || [],
        lastRefreshedAt: (action.payload && action.payload.lastRefreshedAt) || null,
        refreshedBy: (action.payload && action.payload.refreshedBy) || null,
        isLoading: false,
        isRefreshing: false,
        error: null,
      } } };
    case dataDictionaryViewActions.LOAD_USAGE_REPORT_FAILURE:
      return { ...state, reports: { ...state.reports, usage: { ...state.reports.usage, isLoading: false, error: 'Failed to load Usage report' } } };

    case dataDictionaryViewActions.REFRESH_USAGE_REPORT_REQUEST:
      return { ...state, reports: { ...state.reports, usage: { ...state.reports.usage, isRefreshing: true, error: null } } };
    case dataDictionaryViewActions.REFRESH_USAGE_REPORT_SUCCESS:
      return { ...state, reports: { ...state.reports, usage: {
        ...state.reports.usage,
        isRefreshing: false,
        lastRefreshedAt: (action.payload && action.payload.lastRefreshedAt) || null,
        error: null,
      } } };
    case dataDictionaryViewActions.REFRESH_USAGE_REPORT_FAILURE:
      return { ...state, reports: { ...state.reports, usage: { ...state.reports.usage, isRefreshing: false, error: 'Failed to refresh usage data' } } };

    default:
      return state;
  }
}
